/**
 * Every sound that is not an engine, made from nothing but arithmetic: the
 * .kkrieger way, where a whole game's audio was a small synthesiser and the
 * instructions for it. Each generator returns its samples; `three/audio`
 * makes them once, in a worker, into the sound bank the game plays from.
 *
 * Most real sounds are a few things at once, and so are these:
 *
 * - **A thump**: the low knock of a mass hitting, a sine falling in pitch.
 * - **Grains**: a crunch, a step on gravel, rain, is many tiny noise bursts,
 *   each rung through a resonance of what broke: metal rings, wood is dull.
 * - **Modes**: what is struck rings at its own frequencies, which for a
 *   panel, a bin or a pane are not a harmonic series, and each dies at its
 *   own rate. A bell is the same, with partials founders tune.
 * - **Tones** that glide: birdsong, a gull, a voice's vowel.
 *
 * Generators take a seed, so variants differ and every variant is always
 * the same; and a sample rate, so a low rumble can be made at a lower one.
 */

import { OnePole, Rng, Svf } from './dsp.js';

export type Material = 'metal' | 'glass' | 'wood' | 'plastic' | 'bin' | 'body' | 'stone';
export type StepSurface = 'asphalt' | 'grass' | 'gravel' | 'snow' | 'wood' | 'puddle';
export type Bird = 'finch' | 'blackbird' | 'tit' | 'robin';

export const MATERIALS: readonly Material[] = ['metal', 'glass', 'wood', 'plastic', 'bin', 'body', 'stone'];
export const STEP_SURFACES: readonly StepSurface[] = ['asphalt', 'grass', 'gravel', 'snow', 'wood', 'puddle'];
export const BIRDS: readonly Bird[] = ['finch', 'blackbird', 'tit', 'robin'];

// --- the primitives -------------------------------------------------------------

/**
 * A tone from `start` s, `dur` long, gliding from `f0` to `f1` Hz (along a
 * curve, `bend` > 1 lingering high), rising over `attack` s and falling to
 * nothing by its end, with `harmonics` (amplitudes of the 1st, 2nd…) and an
 * optional vibrato.
 */
function tone(
  buf: Float32Array, sr: number, start: number, dur: number, f0: number, f1: number, amp: number,
  { attack = 0.01, harmonics = [1], vibrato = 0, vibratoHz = 0, bend = 1 }:
  { attack?: number; harmonics?: number[]; vibrato?: number; vibratoHz?: number; bend?: number } = {},
): void {
  const i0 = Math.max(0, Math.floor(start * sr)), n = Math.min(buf.length - i0, Math.floor(dur * sr));
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr, u = i / n;
    const f = (f0 + (f1 - f0) * Math.pow(u, bend)) * (1 + vibrato * Math.sin(2 * Math.PI * vibratoHz * t));
    phase += f / sr;
    const env = Math.min(1, t / attack) * Math.pow(1 - u, 1.5);
    let s = 0;
    for (let h = 0; h < harmonics.length; h++) s += harmonics[h] * Math.sin(2 * Math.PI * phase * (h + 1));
    buf[i0 + i] += s * env * amp;
  }
}

/** A struck body's ringing: modes at `f0` × each ratio, each dying over its own time constant. */
function modes(
  buf: Float32Array, sr: number, start: number, f0: number,
  ratios: readonly number[], decays: readonly number[], amps: readonly number[], amp: number, rng: Rng,
): void {
  const i0 = Math.floor(start * sr);
  for (let m = 0; m < ratios.length; m++) {
    // A real panel's modes are never quite where the textbook puts them.
    const f = f0 * ratios[m] * (1 + 0.02 * rng.white());
    if (f > sr * 0.45) continue;
    const tau = decays[m], a = amps[m] * amp;
    const n = Math.min(buf.length - i0, Math.floor(tau * 6 * sr));
    const phase0 = rng.next() * 2 * Math.PI;
    const k = Math.exp(-1 / (tau * sr));
    let env = a;
    for (let i = 0; i < n; i++) {
      buf[i0 + i] += Math.sin(phase0 + (2 * Math.PI * f * i) / sr) * env * Math.min(1, i / (sr * 0.0015));
      env *= k;
    }
  }
}

/** A burst of noise `len` s long through a resonance at `freq` Hz, ringing on after by its Q. */
function grain(buf: Float32Array, sr: number, rng: Rng, start: number, len: number, freq: number, q: number, amp: number, svf: Svf): void {
  const i0 = Math.floor(start * sr);
  if (i0 >= buf.length) return;
  svf.set(freq, q, sr);
  const burst = Math.max(1, Math.floor(len * sr));
  const ring = Math.floor((q / (Math.PI * freq)) * 5 * sr);
  const n = Math.min(buf.length - i0, burst + ring);
  for (let i = 0; i < n; i++) {
    const x = i < burst ? rng.white() * Math.min(1, i / Math.max(1, sr * 0.0002)) * (1 - i / burst) : 0;
    svf.process(x);
    buf[i0 + i] += svf.bp * amp;
  }
  // The filter starts the next grain clean.
  for (let i = 0; i < 8; i++) svf.process(0);
}

/** Noise from `start`, `dur` long, through a filter, shaped by `env(u)` over u 0..1. */
function noise(
  buf: Float32Array, sr: number, rng: Rng, start: number, dur: number,
  filter: { type: 'lp' | 'bp' | 'hp'; freq: number; q?: number; sweepTo?: number }, amp: number, env: (u: number) => number,
): void {
  const i0 = Math.max(0, Math.floor(start * sr)), n = Math.min(buf.length - i0, Math.floor(dur * sr));
  const svf = new Svf();
  for (let i = 0; i < n; i++) {
    const u = i / n;
    if (i % 32 === 0) svf.set(filter.freq + ((filter.sweepTo ?? filter.freq) - filter.freq) * u, filter.q ?? 0.707, sr);
    svf.process(rng.white());
    const y = filter.type === 'lp' ? svf.lp : filter.type === 'bp' ? svf.bp : svf.hp;
    buf[i0 + i] += y * env(u) * amp;
  }
}

/** A thump: a sine falling from `f0` to half, dying over `tau` s. */
function thump(buf: Float32Array, sr: number, start: number, f0: number, tau: number, amp: number): void {
  const i0 = Math.floor(start * sr), n = Math.min(buf.length - i0, Math.floor(tau * 6 * sr));
  let phase = 0;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    phase += (f0 * (0.5 + 0.5 * Math.exp(-t / (tau * 0.7)))) / sr;
    buf[i0 + i] += Math.sin(2 * Math.PI * phase) * Math.exp(-t / tau) * Math.min(1, t / 0.002) * amp;
  }
}

/** Scale to a peak of `peak`. */
function normalize(buf: Float32Array, peak = 0.9): Float32Array {
  let max = 0;
  for (const s of buf) max = Math.max(max, Math.abs(s));
  if (max > 0) for (let i = 0; i < buf.length; i++) buf[i] *= peak / max;
  return buf;
}

/**
 * A loop: `make` draws into a buffer longer than the loop, and what runs
 * past the end is folded back over the start, so it repeats without a seam.
 */
function loop(seconds: number, sr: number, tail: number, make: (buf: Float32Array) => void): Float32Array {
  const n = Math.floor(seconds * sr);
  const long = new Float32Array(n + Math.floor(tail * sr));
  make(long);
  const out = long.slice(0, n);
  for (let i = n; i < long.length; i++) out[i - n] += long[i];
  return out;
}

// --- crashes ---------------------------------------------------------------------

interface MaterialSound {
  /** The thump's pitch, Hz. */
  thump: number;
  /** The crunch: grains for a full hit, their band, Hz, and Q. */
  grains: number;
  band: [number, number];
  q: number;
  /** Ringing modes: base, Hz (at a full hit), ratios, time constants, amplitudes. */
  f0: number;
  ratios: number[];
  decays: number[];
  amps: number[];
  ring: number;
}

const MATERIAL: Record<Exclude<Material, 'metal'>, MaterialSound> = {
  // A pane: a bright crack and its ring, high and short.
  glass: { thump: 95, grains: 60, band: [2200, 9000], q: 12, f0: 2100, ratios: [1, 1.73, 2.31, 3.1, 4.2], decays: [0.18, 0.12, 0.09, 0.07, 0.05], amps: [1, 0.8, 0.6, 0.5, 0.35], ring: 0.5 },
  wood: { thump: 105, grains: 70, band: [250, 1800], q: 3, f0: 210, ratios: [1, 2.32, 3.68, 5.1], decays: [0.09, 0.06, 0.04, 0.03], amps: [1, 0.6, 0.4, 0.25], ring: 0.6 },
  plastic: { thump: 125, grains: 50, band: [400, 3000], q: 2.5, f0: 260, ratios: [1, 1.9, 2.8], decays: [0.05, 0.035, 0.025], amps: [1, 0.6, 0.4], ring: 0.35 },
  // A litter bin: a hollow drum that clangs and boings.
  bin: { thump: 90, grains: 40, band: [300, 2600], q: 6, f0: 150, ratios: [1, 1.62, 2.41, 3.13, 3.9], decays: [0.7, 0.5, 0.35, 0.25, 0.2], amps: [1, 0.75, 0.5, 0.4, 0.3], ring: 0.9 },
  // A person: soft, heavy, no ring.
  body: { thump: 62, grains: 18, band: [150, 900], q: 1.2, f0: 0, ratios: [], decays: [], amps: [], ring: 0 },
  stone: { thump: 80, grains: 60, band: [800, 5000], q: 4, f0: 620, ratios: [1, 2.2, 3.4], decays: [0.03, 0.02, 0.015], amps: [1, 0.5, 0.3], ring: 0.4 },
};

/**
 * A crash into `material`, `strength` 0 a knock .. 1 a hard hit: the thump,
 * the crunch of it giving, the ring of what was struck; for glass the shards
 * coming down, for metal the bits that broke off landing after.
 */
export function impact(material: Material, strength: number, seed: number, sr: number): Float32Array {
  if (material === 'metal') return metalImpact(strength, seed, sr);
  const rng = new Rng(seed);
  const m = MATERIAL[material];
  const s = Math.max(0.05, Math.min(1, strength));
  const crunch = 0.06 + 0.3 * s;
  const tinkle = material === 'glass' ? 0.6 + 1.2 * s : 0;
  const ringFor = m.decays.length > 0 ? m.decays[0] * 5 : 0;
  const buf = new Float32Array(Math.ceil(sr * Math.min(3.2, 0.3 + Math.max(crunch + ringFor, tinkle))));

  thump(buf, sr, 0, m.thump * (1.2 - 0.3 * s), 0.05 + 0.1 * s, 0.9 * (0.4 + 0.6 * s));
  // The crunch: front-loaded, the material giving way.
  const svf = new Svf();
  const count = Math.round(m.grains * (0.15 + 0.85 * s));
  for (let g = 0; g < count; g++) {
    const u = rng.next();
    const t = crunch * u * u;
    const f = m.band[0] * Math.pow(m.band[1] / m.band[0], rng.next());
    grain(buf, sr, rng, t, 0.0004 + 0.003 * rng.next(), f, m.q * (0.6 + 0.8 * rng.next()), 0.35 * (1 - 0.7 * u) * (0.4 + 0.6 * rng.next()), svf);
  }
  // The ring: a heavier hit drives the bigger, lower modes.
  if (m.ratios.length > 0) modes(buf, sr, 0.004, m.f0 * (1.15 - 0.3 * s), m.ratios, m.decays, m.amps, m.ring * (0.3 + 0.7 * s), rng);
  // Glass: the shards, falling and skittering, thinning out.
  if (material === 'glass') shards(buf, sr, rng, 0.03, tinkle, Math.round(15 + 90 * s), 1);
  return finishImpact(buf, sr, 0.6 + 0.35 * s);
}

/** End resonances at silence rather than chopping a ringing mode mid-cycle. */
function finishImpact(buf: Float32Array, sr: number, peak: number): Float32Array {
  const fade = Math.min(buf.length, Math.ceil(sr * 0.025));
  for (let i = 0; i < fade; i++) buf[buf.length - 1 - i] *= i / fade;
  return normalize(buf, peak);
}

/** Sheet steel: contact, buckling panels, then loose trim settling. Not a bell. */
function metalImpact(strength: number, seed: number, sr: number): Float32Array {
  const s = Math.max(0.05, Math.min(1, strength)), rng = new Rng(seed);
  const damage = Math.max(0, (s - 0.35) / 0.65);
  const buf = new Float32Array(Math.ceil(sr * (0.65 + s * 1.5)));
  const size = 0.85 + rng.next() * 0.3;
  thump(buf, sr, 0, (95 - s * 32) * size, 0.035 + s * 0.055, 0.85);
  noise(buf, sr, rng, 0, 0.025 + s * 0.025, { type: 'bp', freq: 1800, q: 0.7, sweepTo: 650 },
    0.65, u => Math.min(1, u * 35) * Math.exp(-u * 6));
  // Attached panels are damped by seams, trim and the vehicle's mass.
  const ratios = [1, 1.47, 2.09, 2.83, 3.91, 5.37];
  modes(buf, sr, 0.002, (290 - s * 100) * size, ratios,
    [0.12, 0.085, 0.065, 0.045, 0.03, 0.018], [1, 0.65, 0.5, 0.35, 0.22, 0.12], 0.22, rng);
  const svf = new Svf(), crush = 0.09 + damage * 0.48;
  // Separate folds: correlated tearing bursts with a dull panel knock under
  // each. A small knock has neither a long crunch nor falling debris.
  for (let k = 0; k < 2 + Math.round(damage * 8); k++) {
    const u = k / (2 + Math.round(damage * 8));
    const t = 0.008 + crush * (u * u + rng.next() * 0.035);
    const a = (0.12 + damage * 0.6) * (1 - u * 0.65);
    noise(buf, sr, rng, t, 0.018 + rng.next() * 0.045, { type: 'bp', freq: 650 + rng.next() * 1600, q: 0.8, sweepTo: 450 },
      a, v => Math.min(1, v * 18) * (1 - v) ** 2);
    modes(buf, sr, t, (110 + rng.next() * 170) * size, ratios,
      [0.065, 0.045, 0.03, 0.023, 0.016, 0.01], [1, 0.6, 0.4, 0.25, 0.15, 0.08], a * 0.32, rng);
    for (let j = 0; j < 5; j++) grain(buf, sr, rng, t + rng.next() * 0.03, 0.001 + rng.next() * 0.003,
      900 + rng.next() * 5200, 1.5 + rng.next() * 2, a * 0.4, svf);
  }
  if (damage > 0) {
    for (let k = 0; k < 2 + Math.round(damage * 4); k++) {
      const t = crush + 0.06 + rng.next() * (0.25 + damage * 0.55);
      modes(buf, sr, t, 450 + rng.next() * 1000, [1, 1.71, 2.63], [0.045, 0.025, 0.012], [1, 0.5, 0.2],
        damage * (0.025 + rng.next() * 0.05), rng);
    }
  }
  return finishImpact(buf, sr, 0.6 + 0.35 * s);
}

/** Glass shards landing: high pings, thinning out over `dur` s; `fine` small crumbs instead of pieces. */
function shards(buf: Float32Array, sr: number, rng: Rng, start: number, dur: number, count: number, fine: number): void {
  for (let k = 0; k < count; k++) {
    const u = Math.pow(rng.next(), 1.6);
    const t = start + u * dur;
    const f = (2600 + 6400 * rng.next()) * (fine > 1 ? 1.25 : 1);
    const a = (0.05 + 0.25 * rng.next()) * (1 - 0.8 * u) / Math.sqrt(fine);
    modes(buf, sr, t, f, [1, 1.47 + 0.2 * rng.next(), 2.21], [0.02 + 0.03 * rng.next(), 0.015, 0.01], [1, 0.6, 0.35], a, rng);
  }
}

/**
 * A bus shelter's toughened glass going: a crack, the burst as the whole
 * pane turns to crumbs at once, and the crumbs pouring and skittering down.
 */
export function shatter(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 2.4));
  thump(buf, sr, 0, 110, 0.06, 0.6);
  noise(buf, sr, rng, 0, 0.35, { type: 'hp', freq: 2500 }, 0.6, u => Math.min(1, u * 400) * Math.exp(-u * 6));
  modes(buf, sr, 0.002, 2300, [1, 1.73, 2.31, 3.1], [0.12, 0.08, 0.06, 0.04], [1, 0.7, 0.5, 0.35], 0.4, rng);
  shards(buf, sr, rng, 0.04, 1.9, 420, 3);
  // The hiss of crumbs pouring onto the pavement.
  noise(buf, sr, rng, 0.05, 1.4, { type: 'bp', freq: 6000, q: 0.8 }, 0.12, u => Math.min(1, u * 20) * (1 - u) ** 2);
  return finishImpact(buf, sr, 0.9);
}

/**
 * Bodywork rubbing a barrier: a low, rough rasp with damped resonances.
 */
export function scrape(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const n = Math.round(1.5 * sr), fade = Math.round(0.06 * sr);
  const buf = new Float32Array(n + fade);
  {
    const a = new Svf(), b = new Svf(), c = new Svf(), judder = new OnePole();
    a.set(320, 1.1, sr); b.set(850, 1.2, sr); c.set(1200, 0.7, sr); judder.set(25, sr);
    let stick = 0;
    for (let i = 0; i < buf.length; i++) {
      if (rng.next() < 70 / sr) stick = 0.5 + rng.next();
      stick *= Math.exp(-1 / (sr * 0.028));
      const grit = rng.white() * (0.35 + judder.process(stick + 0.4 * rng.white()));
      a.process(grit); b.process(grit);
      c.process(a.bp * 0.65 + b.bp * 0.3 + grit * 0.04);
      buf[i] = c.lp;
    }
  }
  // Crossfade the end's continuation into the start, rather than adding a
  // second noise layer that abruptly disappears after the overlap.
  for (let i = 0; i < fade; i++) {
    const t = i / fade * Math.PI / 2;
    buf[i] = buf[n + i] * Math.cos(t) + buf[i] * Math.sin(t);
  }
  return normalize(buf.slice(0, n), 0.7);
}

// --- footsteps -------------------------------------------------------------------

/**
 * One footstep on `surface`: the heel and the roll to the toe. `heavy` is a
 * run's: harder and quicker.
 */
export function footstep(surface: StepSurface, heavy: boolean, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 0.32));
  const hit = heavy ? 1 : 0.7;
  const svf = new Svf();
  switch (surface) {
    case 'asphalt':
      thump(buf, sr, 0, 95, 0.02, 0.5 * hit);
      noise(buf, sr, rng, 0, 0.012, { type: 'hp', freq: 1800 }, 0.5 * hit, u => (1 - u) ** 3);
      noise(buf, sr, rng, heavy ? 0.045 : 0.07, 0.06, { type: 'bp', freq: 3200, q: 0.9 }, 0.18, u => Math.sin(Math.PI * u));
      break;
    case 'grass':
      thump(buf, sr, 0, 70, 0.025, 0.35 * hit);
      noise(buf, sr, rng, 0, 0.15, { type: 'bp', freq: 2600, q: 0.6, sweepTo: 1800 }, 0.35 * hit, u => Math.min(1, u * 8) * (1 - u) ** 2);
      for (let k = 0; k < 14; k++) grain(buf, sr, rng, rng.next() * 0.14, 0.002, 3000 + 3000 * rng.next(), 1.5, 0.12, svf);
      break;
    case 'gravel':
      thump(buf, sr, 0, 85, 0.02, 0.35 * hit);
      for (let k = 0; k < (heavy ? 70 : 45); k++) {
        const u = Math.pow(rng.next(), 1.4);
        grain(buf, sr, rng, u * 0.16, 0.0006 + 0.0015 * rng.next(), 1200 * Math.pow(4.5, rng.next()), 3 + 3 * rng.next(), 0.3 * (1 - 0.6 * u), svf);
      }
      break;
    case 'snow':
      thump(buf, sr, 0, 60, 0.03, 0.25 * hit);
      for (let k = 0; k < 40; k++) {
        const u = rng.next();
        grain(buf, sr, rng, u * 0.2, 0.002 + 0.004 * rng.next(), 500 * Math.pow(3.5, rng.next()), 1.8, 0.22 * Math.sin(Math.PI * u), svf);
      }
      // Cold snow squeaks as it packs.
      tone(buf, sr, 0.05, 0.08, 1150, 880, 0.05, { harmonics: [1, 0.4, 0.2] });
      break;
    case 'wood':
      modes(buf, sr, 0, 145, [1, 2.15, 3.6], [0.06, 0.04, 0.025], [1, 0.6, 0.35], 0.55 * hit, rng);
      noise(buf, sr, rng, 0, 0.008, { type: 'hp', freq: 2000 }, 0.35, u => 1 - u);
      break;
    case 'puddle':
      thump(buf, sr, 0, 80, 0.02, 0.3 * hit);
      noise(buf, sr, rng, 0, 0.12, { type: 'bp', freq: 1300, q: 0.8 }, 0.5 * hit, u => Math.min(1, u * 15) * (1 - u) ** 2);
      // Bubbles: each a small rising ring, as a drop's air pocket does.
      for (let k = 0; k < 5; k++) tone(buf, sr, 0.01 + rng.next() * 0.09, 0.02, 900 + 900 * rng.next(), 1700 + 900 * rng.next(), 0.08);
      break;
  }
  return normalize(buf, heavy ? 0.85 : 0.7);
}

// --- the weather ------------------------------------------------------------------

/**
 * Thunder. `near` is a strike within a kilometre or so: a tearing crack first,
 * then the roll. Far off, only the roll is left, low and long, the crack's
 * high end lost in the air.
 */
export function thunder(near: boolean, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * (near ? 7 : 8)));
  if (near) {
    // The crack: a few rips of noise in quick succession.
    for (let k = 0; k < 4; k++) {
      noise(buf, sr, rng, k * 0.04 + rng.next() * 0.03, 0.25 + rng.next() * 0.2, { type: 'hp', freq: 600 + 800 * rng.next() }, 0.7 / (k + 1), u => Math.exp(-u * 5));
    }
  }
  // The roll: brown noise, low-passed, swelling and ebbing as the sound comes
  // in from different lengths of the bolt.
  const bumps = Array.from({ length: 5 + Math.floor(rng.next() * 5) }, () => ({
    t: (near ? 0.15 : 0.4) + rng.next() * (near ? 4 : 5), w: 0.3 + rng.next() * 0.9, a: 0.4 + rng.next() * 0.6,
  }));
  const svf = new Svf();
  svf.set(near ? 220 : 110, 0.6, sr);
  let brown = 0;
  const n = buf.length;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    brown = brown * 0.996 + rng.white() * 0.06;
    let env = 0;
    for (const b of bumps) env += b.a * Math.exp(-(((t - b.t) / b.w) ** 2));
    env *= Math.min(1, t / (near ? 0.05 : 0.4)) * Math.max(0, 1 - t / (n / sr));
    svf.process(brown);
    buf[i] += svf.lp * env * 2.2;
  }
  return normalize(buf, near ? 0.95 : 0.8);
}

/** Rain, a loop: a hiss and thousands of drops; `heavy` a downpour. */
export function rain(heavy: boolean, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  return normalize(loop(4, sr, 0.05, (buf) => {
    noise(buf, sr, rng, 0, buf.length / sr, { type: 'bp', freq: heavy ? 3500 : 5000, q: 0.5 }, heavy ? 0.12 : 0.05, () => 1);
    const svf = new Svf();
    const drops = (heavy ? 2600 : 700) * (buf.length / sr);
    for (let k = 0; k < drops; k++) {
      const t = rng.next() * (buf.length / sr - 0.03);
      grain(buf, sr, rng, t, 0.0003, 2500 + 6000 * rng.next(), 1.2, 0.05 + 0.12 * rng.next(), svf);
      // Some fall in water and ring.
      if (rng.next() < 0.06) tone(buf, sr, t, 0.012, 1400 + 2400 * rng.next(), 2600 + 2400 * rng.next(), 0.04, { attack: 0.001 });
    }
  }), heavy ? 0.7 : 0.45);
}

/** Rain on a car's roof, a loop: dull taps on a panel, the cabin around them. */
export function roofRain(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  return normalize(loop(3, sr, 0.1, (buf) => {
    const svf = new Svf();
    for (let k = 0; k < 900 * 3; k++) {
      const t = rng.next() * 2.95;
      grain(buf, sr, rng, t, 0.0008, 260 + 500 * rng.next(), 4, 0.1 + 0.15 * rng.next(), svf);
    }
    noise(buf, sr, rng, 0, buf.length / sr, { type: 'lp', freq: 900 }, 0.08, () => 1);
  }), 0.6);
}

// --- life about ---------------------------------------------------------------------

/**
 * A church bell, struck once: the partials founders tune (the hum an octave
 * down, the prime, a minor third, the fifth, the nominal an octave up, and
 * the rest), each dying at its own pace, the hum last; pairs a hair apart
 * beat slowly, as a real bell's do.
 */
export function bell(seed: number, sr: number, f0 = 290): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 6));
  const ratios = [0.5, 1, 1.183, 1.506, 2, 2.514, 2.662, 3.011, 4.166];
  const decays = [2.4, 1.5, 1.2, 0.9, 0.8, 0.5, 0.45, 0.35, 0.22];
  const amps = [0.55, 0.6, 0.5, 0.25, 0.45, 0.25, 0.15, 0.2, 0.12];
  modes(buf, sr, 0, f0, ratios, decays, amps, 0.5, rng);
  modes(buf, sr, 0, f0 * 1.0016, ratios.slice(0, 3), decays.slice(0, 3), amps.slice(0, 3), 0.3, rng);
  noise(buf, sr, rng, 0, 0.01, { type: 'bp', freq: 2500, q: 1 }, 0.4, u => 1 - u);
  return normalize(buf, 0.8);
}

/**
 * A bird's song, by species: the chaffinch's rattling run down with its
 * flourish, the blackbird's fluting phrase, the great tit's see-saw, the
 * robin's thin, quick warble.
 */
export function birdSong(bird: Bird, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 2.8));
  const pitch = 0.92 + 0.16 * rng.next();
  switch (bird) {
    case 'finch': {
      let t = 0.05;
      for (let phrase = 0; phrase < 3; phrase++) {
        const notes = 4 + Math.floor(rng.next() * 3), top = (6200 - phrase * 900) * pitch;
        for (let k = 0; k < notes; k++) {
          tone(buf, sr, t, 0.045, top, top * 0.72, 0.5, { attack: 0.004, harmonics: [1, 0.15] });
          t += 0.075 - phrase * 0.012;
        }
      }
      // The flourish.
      tone(buf, sr, t + 0.03, 0.22, 3200 * pitch, 5600 * pitch, 0.6, { attack: 0.01, bend: 0.5, vibrato: 0.08, vibratoHz: 28 });
      break;
    }
    case 'blackbird': {
      let t = 0.05, f = (1700 + 600 * rng.next()) * pitch;
      for (let k = 0; k < 6 + Math.floor(rng.next() * 3); k++) {
        const next = Math.max(1400, Math.min(3300, f * (0.8 + 0.45 * rng.next())));
        const dur = 0.09 + 0.18 * rng.next();
        tone(buf, sr, t, dur, f, next, 0.55, { attack: 0.015, harmonics: [1, 0.08, 0.03], vibrato: 0.012, vibratoHz: 30 });
        t += dur + 0.02 + 0.06 * rng.next();
        f = next;
      }
      // Ending in a squeaky twitter.
      for (let k = 0; k < 5; k++, t += 0.035) tone(buf, sr, t, 0.025, 5200 * pitch, 7000 * pitch, 0.18, { attack: 0.003 });
      break;
    }
    case 'tit': {
      const hi = 5600 * pitch, lo = 4100 * pitch;
      for (let k = 0, t = 0.05; k < 4 + Math.floor(rng.next() * 3); k++, t += 0.3) {
        tone(buf, sr, t, 0.11, hi, hi * 0.97, 0.5, { attack: 0.006 });
        tone(buf, sr, t + 0.15, 0.1, lo, lo * 0.94, 0.45, { attack: 0.006 });
      }
      break;
    }
    case 'robin': {
      let t = 0.05;
      for (let k = 0; k < 12 + Math.floor(rng.next() * 8); k++) {
        const f0 = (3000 + 4500 * rng.next()) * pitch, f1 = (3000 + 4500 * rng.next()) * pitch;
        const dur = 0.03 + 0.09 * rng.next();
        tone(buf, sr, t, dur, f0, f1, 0.4, { attack: 0.004, vibrato: 0.04 * rng.next(), vibratoHz: 40 });
        t += dur + 0.01 + 0.05 * rng.next();
        if (t > 2.5) break;
      }
      break;
    }
  }
  return normalize(buf, 0.7);
}

/** A herring gull's long call: a rising, crying "kee-ow", then laughing "ha-ha-ha". */
export function gull(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 2.2));
  const p = 0.9 + 0.2 * rng.next();
  const harmonics = [1, 0.7, 0.5, 0.35, 0.2, 0.12];
  tone(buf, sr, 0.02, 0.5, 950 * p, 1500 * p, 0.45, { attack: 0.04, harmonics, bend: 0.4, vibrato: 0.015, vibratoHz: 22 });
  tone(buf, sr, 0.5, 0.35, 1450 * p, 1050 * p, 0.4, { attack: 0.02, harmonics, vibrato: 0.02, vibratoHz: 18 });
  for (let k = 0, t = 1.0; k < 3 + Math.floor(rng.next() * 3); k++, t += 0.17) {
    tone(buf, sr, t, 0.1, 880 * p, 760 * p, 0.35, { attack: 0.01, harmonics });
  }
  noise(buf, sr, rng, 0.02, 0.8, { type: 'bp', freq: 2400, q: 1 }, 0.04, u => Math.sin(Math.PI * u));
  return normalize(buf, 0.6);
}

/** Crickets on a warm night, a loop: chirps of three or four pulses on a high carrier, several insects out of step. */
export function crickets(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  return normalize(loop(3, sr, 0.2, (buf) => {
    for (let c = 0; c < 4; c++) {
      const carrier = 4200 + 900 * rng.next(), rate = 1.6 + 1.4 * rng.next(), amp = 0.15 + 0.25 * rng.next();
      for (let t = rng.next() / rate; t < 2.95; t += 1 / rate) {
        for (let k = 0; k < 3 + Math.floor(rng.next() * 2); k++) tone(buf, sr, t + k * 0.033, 0.022, carrier, carrier * 0.99, amp, { attack: 0.003 });
      }
    }
  }), 0.4);
}

/**
 * Town chatter, a loop: a few people talking, out of earshot of the words.
 * Each voice has its own pitch and size of throat (which moves its
 * formants); it speaks in phrases of syllables, each a consonant (a hiss or
 * a tap) and a vowel: the voice's soft buzz through its two lowest
 * formants, the pitch falling through the phrase as speech does, then a
 * pause while someone else talks.
 */
export function babble(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const vowels: Array<[number, number]> = [[730, 1090], [530, 1840], [270, 2290], [570, 840], [300, 870], [660, 1720], [490, 1350], [440, 1020]];
  return normalize(loop(8, sr, 0.4, (buf) => {
    const f1 = new Svf(), f2 = new Svf(), f3 = new Svf(), hiss = new Svf(), glottis = new OnePole();
    glottis.set(900, sr);
    for (let v = 0; v < 5; v++) {
      const male = rng.next() < 0.5;
      const base = male ? 100 + 30 * rng.next() : 185 + 50 * rng.next();
      const tract = male ? 0.9 + 0.1 * rng.next() : 1.08 + 0.12 * rng.next();
      const loud = 0.5 + 0.5 * rng.next();
      let t = rng.next() * 2;
      while (t < 7.8) {
        const syllables = 3 + Math.floor(rng.next() * 9);
        for (let k = 0; k < syllables && t < 7.8; k++) {
          // The consonant: a hiss (s, f, sh) or a tap (t, k, p), or none.
          const c = rng.next();
          if (c < 0.35) {
            hiss.set(3000 + 3500 * rng.next(), 1.2, sr);
            const n = Math.floor((0.04 + 0.05 * rng.next()) * sr), i0 = Math.floor(t * sr);
            for (let i = 0; i < n && i0 + i < buf.length; i++) {
              hiss.process(rng.white());
              buf[i0 + i] += hiss.bp * Math.sin((Math.PI * i) / n) * 0.12 * loud;
            }
            t += n / sr;
          } else if (c < 0.6) {
            noise(buf, sr, rng, t, 0.012, { type: 'hp', freq: 1800 }, 0.15 * loud, u => 1 - u);
            t += 0.02;
          }
          // The vowel.
          const [a, b] = vowels[Math.floor(rng.next() * vowels.length)];
          f1.set(a * tract, 3.5, sr); f2.set(b * tract, 5, sr); f3.set(2500 * tract, 6, sr);
          const dur = 0.07 + 0.14 * rng.next(), i0 = Math.floor(t * sr), n = Math.floor(dur * sr);
          // Declination: the pitch sinks through the phrase, with a lift on the stressed syllables.
          const pitch = base * (1.12 - 0.22 * (k / syllables)) * (rng.next() < 0.3 ? 1.15 : 1);
          const accent = 0.6 + 0.4 * rng.next();
          let phase = rng.next();
          for (let i = 0; i < n && i0 + i < buf.length; i++) {
            phase += (pitch * (1 + 0.02 * Math.sin(i / 400))) / sr;
            let pulse = 0;
            if (phase >= 1) {
              phase -= 1;
              pulse = 1;
            }
            const src = glottis.process(pulse * 6) + rng.white() * 0.03;
            f1.process(src); f2.process(src); f3.process(src);
            const env = Math.sin((Math.PI * i) / n) ** 0.7;
            buf[i0 + i] += (f1.bp + 0.55 * f2.bp + 0.2 * f3.bp) * env * accent * loud;
          }
          t += dur + 0.015 + 0.04 * rng.next();
        }
        // Someone else's turn.
        t += 0.6 + 2.2 * rng.next();
      }
    }
  }), 0.5);
}

/**
 * A breath: out through the mouth, or in, `effort` 0..1 as the run goes on.
 * Air, not a hit: it swells in over a fifth of its length and dies away as
 * smoothly, its noise kept soft (below 3.5 kHz) and wavering slowly, as air
 * through a mouth does. A sharp start or a bare hiss reads as a tick.
 */
export function breath(exhale: boolean, effort: number, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const dur = exhale ? 0.42 : 0.46;
  const buf = new Float32Array(Math.ceil(sr * dur));
  const a = new Svf(), b = new Svf(), soft = new Svf(), sway = new Svf();
  a.set(exhale ? 650 : 1100, 1.2, sr);
  b.set(exhale ? 1400 : 2300, 1.6, sr);
  soft.set(3500, 0.7, sr);
  sway.set(9, 0.7, sr);
  const n = buf.length;
  // In through the nose and mouth swells slower than a breath pushed out.
  const rise = exhale ? 0.18 : 0.3;
  for (let i = 0; i < n; i++) {
    const u = i / n;
    const env = u < rise ? 0.5 - 0.5 * Math.cos((Math.PI * u) / rise) : (0.5 + 0.5 * Math.cos((Math.PI * (u - rise)) / (1 - rise))) ** 1.4;
    const x = rng.white();
    a.process(x); b.process(x);
    sway.process(rng.white());
    soft.process(a.bp + 0.6 * b.bp);
    buf[i] = soft.lp * env * (1 + 6 * sway.lp);
  }
  return normalize(buf, 0.2 + 0.3 * effort);
}

/** A lorry's or a bus's air brakes letting go: a sharp, hissing "pssht". */
export function airBrake(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 1.3));
  noise(buf, sr, rng, 0, 1.2, { type: 'hp', freq: 2400 }, 0.6, u => Math.min(1, u * 60) * Math.exp(-u * 3.5));
  noise(buf, sr, rng, 0, 0.9, { type: 'bp', freq: 4300, q: 6 }, 0.25, u => Math.min(1, u * 40) * Math.exp(-u * 4));
  return normalize(buf, 0.6);
}

/** A car door: `shut` the slam (latch, thump, the body's rattle), else the latch opening. */
export function door(shut: boolean, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 0.5));
  const svf = new Svf();
  if (shut) {
    thump(buf, sr, 0.005, 68, 0.06, 0.9);
    grain(buf, sr, rng, 0, 0.002, 2600, 4, 0.5, svf);
    grain(buf, sr, rng, 0.012, 0.002, 1800, 5, 0.35, svf);
    modes(buf, sr, 0.004, 190, [1, 1.7, 2.6], [0.08, 0.05, 0.03], [1, 0.6, 0.4], 0.2, rng);
  } else {
    grain(buf, sr, rng, 0, 0.002, 2400, 5, 0.5, svf);
    grain(buf, sr, rng, 0.05, 0.003, 1500, 3, 0.3, svf);
    thump(buf, sr, 0.05, 120, 0.02, 0.2);
  }
  return normalize(buf, shut ? 0.85 : 0.5);
}

/** A parachute opening: the canopy cracking full of air with a deep whump. */
export function canopyOpen(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.ceil(sr * 1.2));
  noise(buf, sr, rng, 0, 0.5, { type: 'bp', freq: 900, q: 0.7, sweepTo: 300 }, 0.6, u => Math.min(1, u * 30) * (1 - u) ** 2);
  thump(buf, sr, 0.08, 55, 0.15, 0.9);
  for (let k = 0; k < 6; k++) noise(buf, sr, rng, 0.2 + k * 0.08, 0.05, { type: 'bp', freq: 600, q: 1 }, 0.25 / (k + 1), u => Math.sin(Math.PI * u));
  return normalize(buf, 0.85);
}

/**
 * Plain noise, a loop, for the beds the game filters live: white, or brown
 * (each sample a step from the last, all rumble), the stuff of wind, a town's
 * hum and the sea.
 */
export function noiseLoop(color: 'white' | 'brown', seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.floor(sr * 4));
  let y = 0;
  for (let i = 0; i < buf.length; i++) {
    y = color === 'white' ? rng.white() : y * 0.995 + rng.white() * 0.1;
    buf[i] = y;
  }
  // Brown noise wanders: take out its drift so the loop meets itself.
  if (color === 'brown') {
    const ramp = buf[buf.length - 1] - buf[0];
    for (let i = 0; i < buf.length; i++) buf[i] -= (ramp * i) / buf.length;
  }
  return normalize(buf, 0.8);
}

// --- trains ----------------------------------------------------------------------

/**
 * A train rolling, a loop: steel wheels on steel rail, a deep roar with a
 * hiss over it and the body's low drone. Played faster and louder the faster
 * it goes (`three/audio/trainSound.ts`).
 */
export function trainRoll(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  return normalize(loop(3, sr, 0.2, (buf) => {
    const dur = buf.length / sr;
    noise(buf, sr, rng, 0, dur, { type: 'lp', freq: 420, q: 0.9 }, 1.0, () => 1);
    noise(buf, sr, rng, 0, dur, { type: 'bp', freq: 1700, q: 1.6 }, 0.22, () => 1);
    // The body's drone, swelling and easing as the wheels go over the rail's waves.
    for (let i = 0; i < buf.length; i++) {
      const t = i / sr;
      buf[i] += 0.25 * Math.sin(2 * Math.PI * 47 * t) * (0.7 + 0.3 * Math.sin(2 * Math.PI * (2 / 3) * t));
    }
  }), 0.7);
}

/**
 * A train's motors, a loop: an electric unit's traction whine, a few
 * gear-and-inverter tones over the mains hum; or a diesel's slow, heavy
 * beat. Every frequency fits the two seconds a whole number of times, so it
 * loops without a seam.
 */
export function trainMotor(electric: boolean, seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.floor(sr * 2));
  // Under it, a rumble: noise with its top taken off, heavier under a diesel.
  let r1 = 0, rumble = 0;
  const smooth = 1 - Math.exp((-2 * Math.PI * 180) / sr);
  for (let i = 0; i < buf.length; i++) {
    const t = i / sr;
    r1 += smooth * (rng.white() - r1);
    rumble += smooth * (r1 - rumble);
    let s: number;
    if (electric) {
      s = 0.5 * Math.sin(2 * Math.PI * 330 * t) + 0.3 * Math.sin(2 * Math.PI * 495 * t) + 0.18 * Math.sin(2 * Math.PI * 660 * t)
        + 0.35 * Math.sin(2 * Math.PI * 100 * t) + 0.12 * Math.sin(2 * Math.PI * 1320 * t);
    } else {
      // Firing at 18 Hz: a pulse train with its harmonics, rough at the top.
      const ph = (t * 18) % 1;
      // Each beat swelling in over a few milliseconds: begun at full, it would click.
      s = (1 - Math.exp(-ph * 40)) * Math.exp(-ph * 9) * (Math.sin(2 * Math.PI * 36 * t) + 0.6 * Math.sin(2 * Math.PI * 72 * t) + 0.3 * Math.sin(2 * Math.PI * 108 * t));
    }
    buf[i] = s + (electric ? 0.3 : 1.2) * rumble;
  }
  return normalize(buf, 0.6);
}

/**
 * One wheel over a rail joint: a hard knock and the rail's short ring.
 * Each axle of a bogie plays one, the second a moment after the first: the
 * clickety-clack.
 */
export function trainClack(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.floor(sr * 0.35));
  thump(buf, sr, 0, 85, 0.03, 0.9);
  const svf = new Svf();
  grain(buf, sr, rng, 0, 0.004, 2400, 5, 0.6, svf);
  modes(buf, sr, 0, 760, [1, 2.7, 4.4], [0.08, 0.05, 0.03], [1, 0.6, 0.35], 0.25, rng);
  return normalize(buf, 0.8);
}

/**
 * A train's horn: two notes at once, a reedy, strident chord, swelling in
 * and cut off.
 */
export function trainHorn(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const dur = 1.4;
  const buf = new Float32Array(Math.floor(sr * (dur + 0.2)));
  const notes = [311, 370];
  for (let i = 0; i < Math.floor(dur * sr); i++) {
    const t = i / sr;
    const env = Math.min(1, t / 0.06) * Math.min(1, (dur - t) / 0.12);
    let s = 0;
    for (const f of notes) {
      // A reed's spectrum: odd and even harmonics falling off slowly, a breath of wobble.
      const w = f * (1 + 0.003 * Math.sin(2 * Math.PI * 5 * t));
      for (let h = 1; h <= 8; h++) s += Math.sin(2 * Math.PI * w * h * t) / h;
    }
    buf[i] = s * env + 0.02 * rng.white() * env;
  }
  return normalize(buf, 0.8);
}

/**
 * A level crossing's bell: one bright struck ding, a hard strike and a ring
 * of a few inharmonic modes, short enough to repeat twice a second as the
 * crossing rings (`three/railCrossings.ts`).
 */
export function crossingBell(seed: number, sr: number): Float32Array {
  const rng = new Rng(seed);
  const buf = new Float32Array(Math.floor(sr * 0.45));
  const svf = new Svf();
  grain(buf, sr, rng, 0, 0.002, 3200, 6, 0.35, svf);
  modes(buf, sr, 0, 1180, [1, 2.32, 2.92, 4.1], [0.22, 0.12, 0.08, 0.05], [1, 0.55, 0.35, 0.2], 0.9, rng);
  return normalize(buf, 0.8);
}

/**
 * A train's doors closing: three quick high beeps, the warning before they
 * shut, then the thud of the leaves meeting.
 */
export function trainDoors(seed: number, sr: number): Float32Array {
  const buf = new Float32Array(Math.floor(sr * 1.3));
  for (let k = 0; k < 3; k++) {
    const start = k * 0.22, n = Math.floor(sr * 0.13), i0 = Math.floor(start * sr);
    for (let i = 0; i < n; i++) {
      const t = i / sr;
      // Swelling in and out over a few milliseconds each end: no clicks.
      const env = Math.min(1, t / 0.005) * Math.min(1, (0.13 - t) / 0.01);
      buf[i0 + i] += 0.5 * Math.sin(2 * Math.PI * 1480 * t) * env;
    }
  }
  thump(buf, sr, 0.95, 90, 0.05, 0.8);
  const rng = new Rng(seed);
  noise(buf, sr, rng, 0.95, 0.12, { type: 'bp', freq: 900, q: 1.5 }, 0.25, u => Math.min(1, u * 30) * (1 - u));
  return normalize(buf, 0.7);
}

/**
 * An e-scooter's display waking as the rider steps on (or going to sleep as
 * they step off): two short piezo beeps, the second a step up (or down),
 * thin and bright as a 2 kHz buzzer is.
 */
export function scooterBeep(on: boolean, sr: number): Float32Array {
  const buf = new Float32Array(Math.floor(sr * 0.32));
  const notes: Array<[number, number]> = on ? [[0, 2093], [0.13, 2637]] : [[0, 2637], [0.13, 2093]];
  for (const [start, f] of notes) {
    const i0 = Math.floor(start * sr), n = Math.floor(sr * 0.09);
    for (let i = 0; i < n && i0 + i < buf.length; i++) {
      const t = i / sr, u = i / n;
      // Square-ish: a buzzer's odd harmonics, switched on and off hard.
      const env = Math.min(1, t / 0.002) * Math.min(1, (1 - u) / 0.05);
      buf[i0 + i] += (Math.sin(2 * Math.PI * f * t) + 0.3 * Math.sin(6 * Math.PI * f * t) + 0.12 * Math.sin(10 * Math.PI * f * t)) * env;
    }
  }
  return normalize(buf, 0.5);
}

/** A controller connecting: two soft rising notes, a fifth apart, each swelling in from silence. */
export function padChime(sr: number): Float32Array {
  const buf = new Float32Array(Math.floor(sr * 0.5));
  for (const [start, f] of [[0, 660], [0.12, 990]]) {
    const i0 = Math.floor(start * sr), n = Math.floor(sr * 0.3);
    for (let i = 0; i < n && i0 + i < buf.length; i++) {
      const t = i / sr;
      const env = Math.min(1, t / 0.008) * Math.exp(-t / 0.09);
      buf[i0 + i] += (Math.sin(2 * Math.PI * f * t) + 0.25 * Math.sin(4 * Math.PI * f * t)) * env;
    }
  }
  return normalize(buf, 0.6);
}
